import { useState, useEffect } from "react";
import { supabase } from "./supabaseClient";
import { sanitizeText } from './utils/sanitize';
import { Users, BarChart3, FileText, LogOut, Plus, Loader2, Trash2, Edit3, Bell, MessageSquare } from "lucide-react";

const AVATAR_COLORS = ["#dc2626", "#2563eb", "#16a34a", "#7c3aed", "#d97706", "#0891b2"];

// ── AUTH GATE ──
function AdminAuth({ onAuth }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = () => {
    if (password === process.env.REACT_APP_ADMIN_PASSWORD) { onAuth(); }
    else { setError("Incorrect password"); setTimeout(() => setError(""), 2000); }
  };

  return (
    <div style={{ minHeight: "100vh", background: "#0a0a0a", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      <div style={{ background: "#161616", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 20, padding: "48px", width: 400 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 32 }}>
          <div style={{ width: 36, height: 36, background: "#dc2626", borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 900, color: "#fff" }}>B</div>
          <div>
            <p style={{ fontSize: 16, fontWeight: 900, color: "#fff", margin: 0 }}>Binos Admin</p>
            <p style={{ fontSize: 10, color: "#dc2626", margin: 0, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em" }}>Restricted Access</p>
          </div>
        </div>
        <p style={{ fontSize: 12, color: "rgba(255,255,255,0.3)", margin: "0 0 20px", fontWeight: 500 }}>Enter the admin password to continue.</p>
        <input type="password" value={password} onChange={e => setPassword(e.target.value)}
          onKeyDown={e => e.key === "Enter" && handleSubmit()}
          placeholder="Admin password" autoComplete="new-password"
          style={{ width: "100%", padding: "12px 14px", background: "rgba(255,255,255,0.05)", border: `1px solid ${error ? "#dc2626" : "rgba(255,255,255,0.08)"}`, borderRadius: 10, fontSize: 14, color: "#fff", outline: "none", fontFamily: "inherit", boxSizing: "border-box", marginBottom: 12 }} />
        {error && <p style={{ fontSize: 11, color: "#dc2626", margin: "0 0 10px", fontWeight: 600 }}>{error}</p>}
        <button onClick={handleSubmit}
          style={{ width: "100%", padding: "12px", background: "#dc2626", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 800, color: "#fff", cursor: "pointer", fontFamily: "inherit" }}>
          Enter Admin Panel
        </button>
      </div>
    </div>
  );
}

// ── PUBLISH ARTICLE FORM ──
function PublishForm({ onPublish, editArticle, onCancel }) {
  const [form, setForm] = useState({
    author: editArticle?.author || "",
    fund: editArticle?.fund || "",
    initials: editArticle?.avatar || "",
    color: editArticle?.color || "#dc2626",
    snippet: editArticle?.snippet || "",
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const wordCount = form.snippet.trim().split(/\s+/).filter(Boolean).length;

  const handlePublish = async () => {
    if (!form.author || !form.fund || !form.snippet) { alert("Please fill in author, fund, and snippet."); return; }
    setSaving(true);
    const article = {
      author: sanitizeText(form.author),
      fund: sanitizeText(form.fund),
      avatar: form.initials || sanitizeText(form.author).split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase(),
      color: form.color,
      snippet: sanitizeText(form.snippet),
      time: "Just published",
      created_at: new Date().toISOString(),
    };
    await onPublish(article, editArticle?.id);
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
    if (!editArticle) setForm({ author: "", fund: "", initials: "", color: "#dc2626", snippet: "" });
  };

  const inputStyle = { width: "100%", padding: "10px 12px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 9, fontSize: 12, color: "#fff", outline: "none", fontFamily: "inherit", boxSizing: "border-box" };
  const labelStyle = { fontSize: 9, fontWeight: 700, color: "rgba(255,255,255,0.3)", textTransform: "uppercase", letterSpacing: "0.1em", display: "block", marginBottom: 6 };

  return (
    <div style={{ background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 14, overflow: "hidden" }}>
      <div style={{ padding: "16px 20px", borderBottom: "1px solid rgba(255,255,255,0.05)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <p style={{ fontSize: 13, fontWeight: 800, color: "#fff", margin: 0 }}>✦ {editArticle ? "Edit" : "Publish New"} Binos Special</p>
        <span style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", fontWeight: 600 }}>Goes live instantly in News Feed</span>
      </div>
      <div style={{ padding: 20, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        {/* LEFT */}
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <label style={labelStyle}>Author Name</label>
            <input value={form.author} onChange={e => set("author", e.target.value)} placeholder="e.g. Nitin Sharma" style={inputStyle}
              onFocus={e => e.target.style.borderColor = "#dc2626"} onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.08)"} />
          </div>
          <div>
            <label style={labelStyle}>Fund / Organisation</label>
            <input value={form.fund} onChange={e => set("fund", e.target.value)} placeholder="e.g. Antler India" style={inputStyle}
              onFocus={e => e.target.style.borderColor = "#dc2626"} onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.08)"} />
          </div>
          <div>
            <label style={labelStyle}>Author Initials (for avatar)</label>
            <input value={form.initials} onChange={e => set("initials", e.target.value)} placeholder="e.g. NS" maxLength={2} style={inputStyle}
              onFocus={e => e.target.style.borderColor = "#dc2626"} onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.08)"} />
          </div>
          <div>
            <label style={labelStyle}>Avatar Color</label>
            <div style={{ display: "flex", gap: 8, marginTop: 2 }}>
              {AVATAR_COLORS.map(c => (
                <button key={c} onClick={() => set("color", c)}
                  style={{ width: 26, height: 26, background: c, borderRadius: 6, cursor: "pointer", border: form.color === c ? "2px solid #fff" : "2px solid transparent", transform: form.color === c ? "scale(1.15)" : "scale(1)", transition: "all 0.15s" }} />
              ))}
            </div>
          </div>
          <div>
            <label style={labelStyle}>Snippet ({wordCount} words)</label>
            <textarea value={form.snippet} onChange={e => set("snippet", e.target.value)} rows={7}
              placeholder="Paste the VC's insight here — 150 to 300 words works best..."
              style={{ ...inputStyle, resize: "none", lineHeight: 1.65 }}
              onFocus={e => e.target.style.borderColor = "#dc2626"} onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.08)"} />
          </div>
        </div>

        {/* RIGHT — Preview */}
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <label style={labelStyle}>Preview — how it appears in News Feed</label>
            <div style={{ background: "linear-gradient(135deg, rgba(220,38,38,0.08), rgba(220,38,38,0.03))", border: "1px solid rgba(220,38,38,0.2)", borderRadius: 12, padding: 16 }}>
              <span style={{ fontSize: 8, fontWeight: 800, color: "#dc2626", background: "rgba(220,38,38,0.1)", padding: "2px 7px", borderRadius: 100, textTransform: "uppercase", letterSpacing: "0.06em", display: "inline-block", marginBottom: 10 }}>Binos Special</span>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                <div style={{ width: 30, height: 30, background: form.color, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 900, color: "#fff", flexShrink: 0 }}>
                  {form.initials || (form.author ? form.author.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase() : "??")}
                </div>
                <div>
                  <p style={{ fontSize: 12, fontWeight: 800, color: "#fff", margin: 0 }}>{form.author || "Author Name"}</p>
                  <p style={{ fontSize: 10, color: "rgba(255,255,255,0.35)", margin: 0 }}>{form.fund || "Fund Name"} · Just published</p>
                </div>
              </div>
              <p style={{ fontSize: 12, color: "rgba(255,255,255,0.6)", lineHeight: 1.65, margin: 0, fontStyle: "italic" }}>
                "{form.snippet ? (form.snippet.length > 200 ? form.snippet.slice(0, 200) + "..." : form.snippet) : "Your snippet will appear here..."}"
              </p>
            </div>
          </div>

          {/* Checklist */}
          <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 10, padding: 14 }}>
            <label style={{ ...labelStyle, marginBottom: 10 }}>Publishing checklist</label>
            {[
              { label: "Author name filled", ok: !!form.author },
              { label: "Fund name filled", ok: !!form.fund },
              { label: "Snippet written", ok: !!form.snippet },
              { label: "Over 100 words (recommended)", ok: wordCount >= 100 },
            ].map((item, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, color: "rgba(255,255,255,0.5)", marginBottom: 7 }}>
                <span style={{ color: item.ok ? "#16a34a" : "#dc2626", fontWeight: 900 }}>{item.ok ? "✓" : "✗"}</span>
                {item.label}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={{ padding: "14px 20px", borderTop: "1px solid rgba(255,255,255,0.05)", display: "flex", gap: 10, justifyContent: "flex-end" }}>
        {editArticle && <button onClick={onCancel} style={{ padding: "9px 16px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 9, fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.5)", cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>}
        <button onClick={handlePublish} disabled={saving}
          style={{ padding: "9px 20px", background: saved ? "#16a34a" : "#dc2626", border: "none", borderRadius: 9, fontSize: 12, fontWeight: 800, color: "#fff", cursor: saving ? "not-allowed" : "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 8, opacity: saving ? 0.7 : 1 }}>
          {saving && <Loader2 style={{ width: 13, height: 13 }} className="animate-spin" />}
          {saved ? "Published! ✓" : saving ? "Publishing..." : editArticle ? "Save Changes" : "Publish to News Feed ↗"}
        </button>
      </div>
    </div>
  );
}

// ── MAIN ADMIN PAGE ──
export default function AdminPage() {
  const [authed, setAuthed] = useState(() => sessionStorage.getItem("binos_admin") === "true");
  const [activeTab, setActiveTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [articles, setArticles] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editArticle, setEditArticle] = useState(null);
  const [queries, setQueries] = useState([]);
  const [stats, setStats] = useState({ users: 0, decks: 0, active: 0, specials: 0, pending: 0, queries: 0 });
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsData, setAnalyticsData] = useState(null);

  const handleAuth = () => { sessionStorage.setItem("binos_admin", "true"); setAuthed(true); };

  useEffect(() => {
    if (!authed) return;
    fetchData();
  }, [authed]);

  useEffect(() => {
    if (activeTab === "analytics" && authed) fetchAnalytics();
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch users
      const { data: profiles } = await supabase.from("profiles").select("*").order("created_at", { ascending: false });
      setUsers(profiles || []);

      // Fetch pending profiles
      const { data: pendingProfiles } = await supabase.from("profiles").select("*").eq("status", "pending").order("created_at", { ascending: false });
      setPending(pendingProfiles || []);

      // Fetch articles
      const { data: arts } = await supabase.from("binos_specials").select("*").order("created_at", { ascending: false });
      setArticles(arts || []);

      // Fetch contact queries
      const { data: contactQueries } = await supabase.from("contact_queries").select("*").order("created_at", { ascending: false });
      setQueries(contactQueries || []);

      // Fetch usage stats
      const { data: usage } = await supabase.from("usage").select("count");
      const totalDecks = (usage || []).reduce((s, u) => s + (u.count || 0), 0);

      setStats({
        users: profiles?.length || 0,
        decks: totalDecks,
        active: profiles?.filter(p => p.last_active)?.length || 0,
        specials: arts?.length || 0,
        pending: pendingProfiles?.length || 0,
        queries: contactQueries?.length || 0,
      });
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const fetchAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const now = Date.now();
      const sevenDaysAgo = new Date(now - 7 * 24 * 60 * 60 * 1000).toISOString();
      const fourteenDaysAgo = new Date(now - 14 * 24 * 60 * 60 * 1000).toISOString();
      const thirtyDaysAgo = new Date(now - 30 * 24 * 60 * 60 * 1000).toISOString();

      const [
        { data: profilesRaw },
        { data: allUsage },
        { count: founderDecksCount },
        { count: contactQueriesCount },
        { count: binosSpecialsCount },
        { data: recentSignups },
        { data: activityFeed },
        { data: usage7d },
        { data: usage30d },
      ] = await Promise.all([
        supabase.from("profiles").select("*"),
        supabase.from("usage").select("*"),
        supabase.from("founder_decks").select("*", { count: "exact", head: true }),
        supabase.from("contact_queries").select("*", { count: "exact", head: true }),
        supabase.from("binos_specials").select("*", { count: "exact", head: true }),
        supabase.from("profiles").select("created_at").gte("created_at", fourteenDaysAgo),
        supabase.from("activity_feed").select("user_id, type, action"),
        supabase.from("usage").select("user_id").gte("updated_at", sevenDaysAgo),
        supabase.from("usage").select("user_id").gte("updated_at", thirtyDaysAgo),
      ]);

      const allProfiles = profilesRaw || [];
      const usage = allUsage || [];
      const activities = activityFeed || [];

      const approvedProfiles = allProfiles.filter(p => p.status === "approved");
      const pendingProfiles = allProfiles.filter(p => p.status === "pending");
      const rejectedProfiles = allProfiles.filter(p => p.status === "rejected");
      const totalDecks = usage.reduce((s, u) => s + (u.count || 0), 0);

      const activeIds7 = new Set((usage7d || []).map(u => u.user_id));
      const activeIds30 = new Set((usage30d || []).map(u => u.user_id));

      const usageByUser = {};
      usage.forEach(u => {
        if (!u.user_id) return;
        if (!usageByUser[u.user_id]) usageByUser[u.user_id] = { total: 0, lastActive: null };
        usageByUser[u.user_id].total += (u.count || 0);
        const ts = u.updated_at || u.created_at;
        if (ts && (!usageByUser[u.user_id].lastActive || ts > usageByUser[u.user_id].lastActive))
          usageByUser[u.user_id].lastActive = ts;
      });

      const topUsers = Object.entries(usageByUser)
        .sort((a, b) => b[1].total - a[1].total)
        .slice(0, 5)
        .map(([id, data]) => {
          const profile = allProfiles.find(p => p.id === id);
          return { id, name: profile?.full_name || "Unknown", fund: profile?.fund_name || "—", role: profile?.role || "—", status: profile?.status || "unknown", total: data.total, lastActive: data.lastActive };
        });

      const signupsByDay = {};
      for (let i = 13; i >= 0; i--) {
        const d = new Date(now - i * 24 * 60 * 60 * 1000);
        signupsByDay[d.toISOString().slice(0, 10)] = 0;
      }
      (recentSignups || []).forEach(s => {
        const key = s.created_at?.slice(0, 10);
        if (key && key in signupsByDay) signupsByDay[key]++;
      });

      const totalApproved = approvedProfiles.length || 1;
      const match = (a, terms) => terms.some(t => (a.type || "").toLowerCase().includes(t) || (a.action || "").toLowerCase().includes(t));
      const pitchUsers = new Set(activities.filter(a => match(a, ["pitch", "deck", "analys"])).map(a => a.user_id)).size;
      const portfolioUsers = new Set(activities.filter(a => match(a, ["portfolio", "company"])).map(a => a.user_id)).size;
      const memoUsers = new Set(activities.filter(a => match(a, ["memo"])).map(a => a.user_id)).size;
      const ragUsers = new Set(activities.filter(a => match(a, ["memory", "rag", "search"])).map(a => a.user_id)).size;

      const activeIds14 = new Set(usage.filter(u => { const ts = u.updated_at || u.created_at; return ts && ts >= fourteenDaysAgo; }).map(u => u.user_id));
      const atRiskUsers = approvedProfiles
        .filter(p => !activeIds14.has(p.id))
        .map(p => ({ ...p, lastActive: usageByUser[p.id]?.lastActive || null, neverActive: !usageByUser[p.id] }));

      setAnalyticsData({
        totalUsers: allProfiles.length,
        approvedCount: approvedProfiles.length,
        pendingCount: pendingProfiles.length,
        rejectedCount: rejectedProfiles.length,
        totalDecks,
        founderDecks: founderDecksCount || 0,
        contactQueries: contactQueriesCount || 0,
        binosSpecials: binosSpecialsCount || 0,
        activeThisWeek: activeIds7.size,
        approvalRate: allProfiles.length > 0 ? Math.round((approvedProfiles.length / allProfiles.length) * 100) : 0,
        mostActiveUsers: topUsers,
        signupsByDay,
        featureAdoption: {
          pitchSummariser: Math.round((pitchUsers / totalApproved) * 100),
          portfolio: Math.round((portfolioUsers / totalApproved) * 100),
          investmentMemo: Math.round((memoUsers / totalApproved) * 100),
          fundMemory: Math.round((ragUsers / totalApproved) * 100),
        },
        atRiskUsers,
        activeIds7,
        activeIds30,
      });
    } catch (e) { console.error(e); }
    setAnalyticsLoading(false);
  };

  const handlePublish = async (article, editId) => {
    if (editId) {
      await supabase.from("binos_specials").update(article).eq("id", editId);
    } else {
      await supabase.from("binos_specials").insert(article);
    }
    setEditArticle(null);
    fetchData();
  };

  const handleApprove = async (id) => {
    await supabase.from("profiles").update({ status: "approved" }).eq("id", id);
    fetchData();
  };

  const handleReject = async (id) => {
    await supabase.from("profiles").update({ status: "rejected" }).eq("id", id);
    fetchData();
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this article from the News Feed?")) return;
    await supabase.from("binos_specials").delete().eq("id", id);
    fetchData();
  };

  if (!authed) return <AdminAuth onAuth={handleAuth} />;

  const NAV = [
    { id: "users", icon: Users, label: "Users" },
    { id: "pending", icon: Bell, label: "Pending" },
    { id: "specials", icon: FileText, label: "Binos Specials" },
    { id: "publish", icon: Plus, label: "Publish Article" },
    { id: "queries", icon: MessageSquare, label: "Contact Queries" },
    { id: "analytics", icon: BarChart3, label: "Analytics" },
  ];

  return (
    <div style={{ display: "flex", height: "100vh", background: "#0a0a0a", fontFamily: "'DM Sans', system-ui, sans-serif", overflow: "hidden" }}>

      {/* SIDEBAR */}
      <nav style={{ width: 220, background: "#0d0d0d", borderRight: "1px solid rgba(255,255,255,0.06)", display: "flex", flexDirection: "column", flexShrink: 0 }}>
        <div style={{ padding: "20px 16px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 30, height: 30, background: "#dc2626", borderRadius: "7px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 900, color: "#fff" }}>B</div>
            <div>
              <p style={{ fontSize: 13, fontWeight: 900, color: "#fff", margin: 0, letterSpacing: "-0.3px" }}>Binos Admin</p>
              <p style={{ fontSize: 9, color: "#dc2626", margin: 0, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em" }}>Restricted Access</p>
            </div>
          </div>
        </div>

        <div style={{ flex: 1, padding: "12px 10px" }}>
          {NAV.map(item => (
            <button key={item.id} onClick={() => setActiveTab(item.id)}
              style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 9, border: "none", cursor: "pointer", background: activeTab === item.id ? "rgba(220,38,38,0.12)" : "transparent", color: activeTab === item.id ? "#fff" : "rgba(255,255,255,0.4)", borderLeft: `2px solid ${activeTab === item.id ? "#dc2626" : "transparent"}`, fontFamily: "inherit", fontSize: 12, fontWeight: 600, textAlign: "left", marginBottom: 2, transition: "all 0.15s" }}
              onMouseEnter={e => { if (activeTab !== item.id) { e.currentTarget.style.background = "rgba(255,255,255,0.04)"; e.currentTarget.style.color = "rgba(255,255,255,0.7)"; }}}
              onMouseLeave={e => { if (activeTab !== item.id) { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "rgba(255,255,255,0.4)"; }}}>
              <item.icon style={{ width: 16, height: 16, flexShrink: 0 }} />
              {item.label}
            </button>
          ))}
        </div>

        <div style={{ padding: "12px 16px", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 28, height: 28, background: "#dc2626", borderRadius: "7px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 900, color: "#fff" }}>AK</div>
            <div>
              <p style={{ fontSize: 11, fontWeight: 700, color: "#fff", margin: 0 }}>Anas Khakhu</p>
              <p style={{ fontSize: 9, color: "rgba(255,255,255,0.3)", margin: 0 }}>Super Admin</p>
            </div>
          </div>
          <button onClick={() => { sessionStorage.removeItem("binos_admin"); setAuthed(false); }}
            style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "8px 4px", marginTop: 10, background: "none", border: "none", cursor: "pointer", color: "rgba(255,255,255,0.25)", fontSize: 11, fontWeight: 600, fontFamily: "inherit" }}>
            <LogOut style={{ width: 13, height: 13 }} /> Sign out of admin
          </button>
        </div>
      </nav>

      {/* MAIN */}
      <main style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>

        {/* TOPBAR */}
        <div style={{ height: 56, background: "#0d0d0d", borderBottom: "1px solid rgba(255,255,255,0.06)", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 28px", flexShrink: 0 }}>
          <div>
            <p style={{ fontSize: 16, fontWeight: 900, color: "#fff", margin: 0, letterSpacing: "-0.3px" }}>
              {activeTab === "users" ? "Users" : activeTab === "pending" ? "Pending Approvals" : activeTab === "specials" ? "✦ Binos Specials" : activeTab === "publish" ? "Publish Article" : activeTab === "queries" ? "Contact Queries" : "Analytics"}
            </p>
            <p style={{ fontSize: 9, fontWeight: 700, color: "#dc2626", margin: 0, textTransform: "uppercase", letterSpacing: "0.15em" }}>Binos Internal · Restricted</p>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={() => { fetchData(); if (activeTab === "analytics") fetchAnalytics(); }} style={{ padding: "7px 14px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.5)", cursor: "pointer", fontFamily: "inherit" }}>Refresh</button>
            {activeTab !== "publish" && <button onClick={() => setActiveTab("publish")} style={{ padding: "7px 14px", background: "#dc2626", border: "none", borderRadius: 8, fontSize: 11, fontWeight: 800, color: "#fff", cursor: "pointer", fontFamily: "inherit" }}>+ Publish Article</button>}
          </div>
        </div>

        {/* CONTENT */}
        <div style={{ flex: 1, overflowY: "auto", padding: "24px 28px" }}>

          {/* STATS */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(6,1fr)", gap: 12, marginBottom: 24 }}>
            {[
              { label: "Total Users", value: stats.users, sub: "funds registered", color: "#fff" },
              { label: "Pending", value: stats.pending, sub: "awaiting approval", color: "#d97706" },
              { label: "Decks Analysed", value: stats.decks, sub: "all time", color: "#16a34a" },
              { label: "Binos Specials", value: stats.specials, sub: "live in news feed", color: "#dc2626" },
              { label: "Contact Queries", value: stats.queries, sub: "total received", color: "#fff" },
              { label: "API Usage", value: "Live", sub: "serverless proxy", color: "#2563eb" },
            ].map((s, i) => (
              <div key={i} style={{ background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: "16px 18px" }}>
                <p style={{ fontSize: 9, fontWeight: 700, color: "rgba(255,255,255,0.3)", textTransform: "uppercase", letterSpacing: "0.1em", margin: "0 0 8px" }}>{s.label}</p>
                <p style={{ fontSize: 26, fontWeight: 900, color: s.color, letterSpacing: "-1px", margin: "0 0 2px" }}>{s.value}</p>
                <p style={{ fontSize: 10, color: "rgba(255,255,255,0.25)", margin: 0 }}>{s.sub}</p>
              </div>
            ))}
          </div>

          {/* USERS TAB */}
          {activeTab === "users" && (
            <div style={{ background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 14, overflow: "hidden" }}>
              <div style={{ padding: "14px 20px", borderBottom: "1px solid rgba(255,255,255,0.05)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div>
                  <p style={{ fontSize: 13, fontWeight: 800, color: "#fff", margin: 0 }}>All Users</p>
                  <p style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", margin: 0 }}>{users.length} funds registered</p>
                </div>
              </div>
              {loading ? (
                <div style={{ padding: "40px", textAlign: "center" }}>
                  <Loader2 style={{ width: 24, height: 24, color: "#dc2626", margin: "0 auto" }} className="animate-spin" />
                </div>
              ) : users.length === 0 ? (
                <div style={{ padding: "48px", textAlign: "center" }}>
                  <p style={{ fontSize: 13, color: "rgba(255,255,255,0.2)", fontWeight: 600 }}>No users yet</p>
                </div>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: "rgba(255,255,255,0.02)" }}>
                      {["User", "Fund", "Role", "Plan", "Joined", "Actions"].map((h, i) => (
                        <th key={i} style={{ padding: "10px 20px", textAlign: "left", fontSize: 9, fontWeight: 700, color: "rgba(255,255,255,0.25)", textTransform: "uppercase", letterSpacing: "0.1em" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user, i) => (
                      <tr key={i} style={{ borderTop: "1px solid rgba(255,255,255,0.04)" }}>
                        <td style={{ padding: "13px 20px" }}>
                          <p style={{ fontSize: 12, fontWeight: 700, color: "#fff", margin: "0 0 2px" }}>{user.full_name || "—"}</p>
                          <p style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", margin: 0 }}>{user.id?.slice(0, 8)}...</p>
                        </td>
                        <td style={{ padding: "13px 20px", fontSize: 12, color: "rgba(255,255,255,0.6)", fontWeight: 600 }}>{user.fund_name || "—"}</td>
                        <td style={{ padding: "13px 20px", fontSize: 12, color: "rgba(255,255,255,0.4)" }}>{user.role || "—"}</td>
                        <td style={{ padding: "13px 20px" }}>
                          <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 100, fontSize: 9, fontWeight: 800, textTransform: "uppercase", background: "rgba(220,38,38,0.12)", color: "#dc2626" }}>{user.plan || "Early Access"}</span>
                        </td>
                        <td style={{ padding: "13px 20px", fontSize: 11, color: "rgba(255,255,255,0.3)" }}>
                          {user.created_at ? new Date(user.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                        </td>
                        <td style={{ padding: "13px 20px" }}>
                          <button style={{ padding: "5px 10px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 6, fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.4)", cursor: "pointer", fontFamily: "inherit" }}>View</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* PENDING TAB */}
          {activeTab === "pending" && (
            <div style={{ background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 14, overflow: "hidden" }}>
              <div style={{ padding: "14px 20px", borderBottom: "1px solid rgba(255,255,255,0.05)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div>
                  <p style={{ fontSize: 13, fontWeight: 800, color: "#fff", margin: 0 }}>Pending Approvals</p>
                  <p style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", margin: 0 }}>{pending.length} applications awaiting review</p>
                </div>
              </div>
              {loading ? (
                <div style={{ padding: "40px", textAlign: "center" }}>
                  <Loader2 style={{ width: 24, height: 24, color: "#dc2626", margin: "0 auto" }} className="animate-spin" />
                </div>
              ) : pending.length === 0 ? (
                <div style={{ padding: "48px", textAlign: "center" }}>
                  <p style={{ fontSize: 13, color: "rgba(255,255,255,0.2)", fontWeight: 600 }}>No pending applications</p>
                </div>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: "rgba(255,255,255,0.02)" }}>
                      {["Name", "Fund", "Role", "AUM", "Applied Date", "Actions"].map((h, i) => (
                        <th key={i} style={{ padding: "10px 20px", textAlign: "left", fontSize: 9, fontWeight: 700, color: "rgba(255,255,255,0.25)", textTransform: "uppercase", letterSpacing: "0.1em" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pending.map((p, i) => (
                      <tr key={i} style={{ borderTop: "1px solid rgba(255,255,255,0.04)" }}>
                        <td style={{ padding: "13px 20px", fontSize: 12, fontWeight: 700, color: "#fff" }}>{p.full_name || "—"}</td>
                        <td style={{ padding: "13px 20px", fontSize: 12, color: "rgba(255,255,255,0.6)", fontWeight: 600 }}>{p.fund_name || "—"}</td>
                        <td style={{ padding: "13px 20px", fontSize: 12, color: "rgba(255,255,255,0.4)" }}>{p.role || "—"}</td>
                        <td style={{ padding: "13px 20px", fontSize: 12, color: "rgba(255,255,255,0.4)" }}>{p.aum || "—"}</td>
                        <td style={{ padding: "13px 20px", fontSize: 11, color: "rgba(255,255,255,0.3)" }}>
                          {p.created_at ? new Date(p.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                        </td>
                        <td style={{ padding: "13px 20px" }}>
                          <div style={{ display: "flex", gap: 6 }}>
                            <button onClick={() => handleApprove(p.id)}
                              style={{ padding: "5px 12px", background: "rgba(22,163,74,0.12)", border: "1px solid rgba(22,163,74,0.3)", borderRadius: 6, fontSize: 10, fontWeight: 700, color: "#16a34a", cursor: "pointer", fontFamily: "inherit" }}>
                              Approve
                            </button>
                            <button onClick={() => handleReject(p.id)}
                              style={{ padding: "5px 12px", background: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.2)", borderRadius: 6, fontSize: 10, fontWeight: 700, color: "#dc2626", cursor: "pointer", fontFamily: "inherit" }}>
                              Reject
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* SPECIALS TAB */}
          {activeTab === "specials" && (
            <div>
              <div style={{ marginBottom: 16 }}>
                <p style={{ fontSize: 13, fontWeight: 800, color: "#fff", margin: "0 0 4px" }}>✦ Live Articles in News Feed</p>
                <p style={{ fontSize: 11, color: "rgba(255,255,255,0.3)" }}>{articles.length} articles published</p>
              </div>
              {editArticle && (
                <div style={{ marginBottom: 20 }}>
                  <PublishForm onPublish={handlePublish} editArticle={editArticle} onCancel={() => setEditArticle(null)} />
                </div>
              )}
              {articles.length === 0 ? (
                <div style={{ background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 14, padding: "48px", textAlign: "center" }}>
                  <p style={{ fontSize: 13, color: "rgba(255,255,255,0.2)", fontWeight: 600, margin: "0 0 8px" }}>No articles published yet</p>
                  <button onClick={() => setActiveTab("publish")} style={{ padding: "8px 16px", background: "#dc2626", border: "none", borderRadius: 8, fontSize: 12, fontWeight: 700, color: "#fff", cursor: "pointer", fontFamily: "inherit" }}>Publish First Article</button>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {articles.map((article, i) => (
                    <div key={i} style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: "16px 18px", display: "flex", alignItems: "center", gap: 14 }}>
                      <div style={{ width: 36, height: 36, background: article.color || "#dc2626", borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 900, color: "#fff", flexShrink: 0 }}>{article.avatar}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
                          <p style={{ fontSize: 13, fontWeight: 700, color: "#fff", margin: 0 }}>{article.author}</p>
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 9, fontWeight: 700, color: "#16a34a", background: "rgba(22,163,74,0.1)", padding: "2px 7px", borderRadius: 100 }}>
                            <span style={{ width: 4, height: 4, background: "#16a34a", borderRadius: "50%", display: "inline-block" }}></span>Live
                          </span>
                        </div>
                        <p style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", margin: "0 0 5px" }}>{article.fund} · {new Date(article.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</p>
                        <p style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", margin: 0, lineHeight: 1.5, fontStyle: "italic" }}>"{article.snippet?.slice(0, 120)}..."</p>
                      </div>
                      <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                        <button onClick={() => setEditArticle(article)}
                          style={{ padding: "6px 10px", background: "transparent", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 7, fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.4)", cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 5 }}>
                          <Edit3 style={{ width: 11, height: 11 }} /> Edit
                        </button>
                        <button onClick={() => handleDelete(article.id)}
                          style={{ padding: "6px 10px", background: "transparent", border: "1px solid rgba(220,38,38,0.2)", borderRadius: 7, fontSize: 10, fontWeight: 700, color: "#dc2626", cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 5 }}>
                          <Trash2 style={{ width: 11, height: 11 }} /> Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* PUBLISH TAB */}
          {activeTab === "publish" && (
            <PublishForm onPublish={handlePublish} editArticle={null} onCancel={null} />
          )}

          {/* QUERIES TAB */}
          {activeTab === "queries" && (
            <div style={{ background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 14, overflow: "hidden" }}>
              <div style={{ padding: "14px 20px", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                <p style={{ fontSize: 13, fontWeight: 800, color: "#fff", margin: 0 }}>Contact Queries</p>
                <p style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", margin: 0 }}>{queries.length} total received</p>
              </div>
              {loading ? (
                <div style={{ padding: "40px", textAlign: "center" }}>
                  <Loader2 style={{ width: 24, height: 24, color: "#dc2626", margin: "0 auto" }} className="animate-spin" />
                </div>
              ) : queries.length === 0 ? (
                <div style={{ padding: "48px", textAlign: "center" }}>
                  <p style={{ fontSize: 13, color: "rgba(255,255,255,0.2)", fontWeight: 600 }}>No contact queries yet</p>
                </div>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: "rgba(255,255,255,0.02)" }}>
                      {["Name", "Email", "Message", "Received", "Actions"].map((h, i) => (
                        <th key={i} style={{ padding: "10px 20px", textAlign: "left", fontSize: 9, fontWeight: 700, color: "rgba(255,255,255,0.25)", textTransform: "uppercase", letterSpacing: "0.1em" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {queries.map((q, i) => (
                      <tr key={i} style={{ borderTop: "1px solid rgba(255,255,255,0.04)" }}>
                        <td style={{ padding: "13px 20px", fontSize: 12, fontWeight: 700, color: "#fff", whiteSpace: "nowrap" }}>{q.name || "—"}</td>
                        <td style={{ padding: "13px 20px", fontSize: 12, color: "rgba(255,255,255,0.5)", whiteSpace: "nowrap" }}>{q.email || "—"}</td>
                        <td style={{ padding: "13px 20px", fontSize: 12, color: "rgba(255,255,255,0.4)", maxWidth: 320 }}>
                          <p style={{ margin: 0, lineHeight: 1.5, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{q.message || "—"}</p>
                        </td>
                        <td style={{ padding: "13px 20px", fontSize: 11, color: "rgba(255,255,255,0.3)", whiteSpace: "nowrap" }}>
                          {q.created_at ? new Date(q.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                        </td>
                        <td style={{ padding: "13px 20px" }}>
                          <a href={`mailto:${q.email}`}
                            style={{ padding: "5px 12px", background: "rgba(37,99,235,0.12)", border: "1px solid rgba(37,99,235,0.3)", borderRadius: 6, fontSize: 10, fontWeight: 700, color: "#2563eb", cursor: "pointer", fontFamily: "inherit", textDecoration: "none", display: "inline-block" }}>
                            Reply
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* ANALYTICS TAB */}
          {activeTab === "analytics" && (
            <div>
              {analyticsLoading || !analyticsData ? (
                <div style={{ padding: "80px", textAlign: "center" }}>
                  <Loader2 style={{ width: 32, height: 32, color: "#dc2626", margin: "0 auto" }} className="animate-spin" />
                  <p style={{ fontSize: 12, color: "rgba(255,255,255,0.3)", marginTop: 14, fontWeight: 500 }}>Loading analytics...</p>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>

                  {/* SECTION 1 — KEY METRICS */}
                  <div>
                    <p style={{ fontSize: 9, fontWeight: 700, color: "rgba(255,255,255,0.25)", textTransform: "uppercase", letterSpacing: "0.12em", margin: "0 0 12px" }}>Key Metrics</p>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(6,1fr)", gap: 12 }}>
                      {[
                        { label: "Total Users", value: analyticsData.totalUsers, sub: "all registered", color: "#fff" },
                        { label: "Active This Week", value: analyticsData.activeThisWeek, sub: "last 7 days", color: "#16a34a" },
                        { label: "Decks Analysed", value: analyticsData.totalDecks, sub: "all time", color: "#dc2626" },
                        { label: "Founder Decks", value: analyticsData.founderDecks, sub: "in database", color: "#fff" },
                        { label: "Approval Rate", value: `${analyticsData.approvalRate}%`, sub: `${analyticsData.approvedCount} approved`, color: "#2563eb" },
                        { label: "Contact Queries", value: analyticsData.contactQueries, sub: "received", color: "#fff" },
                      ].map((s, i) => (
                        <div key={i} style={{ background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: "16px 18px" }}>
                          <p style={{ fontSize: 9, fontWeight: 700, color: "rgba(255,255,255,0.3)", textTransform: "uppercase", letterSpacing: "0.1em", margin: "0 0 8px" }}>{s.label}</p>
                          <p style={{ fontSize: 26, fontWeight: 900, color: s.color, letterSpacing: "-1px", margin: "0 0 2px" }}>{s.value}</p>
                          <p style={{ fontSize: 10, color: "rgba(255,255,255,0.25)", margin: 0 }}>{s.sub}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* SECTION 2 — SIGNUPS CHART */}
                  <div style={{ background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 14, padding: "20px 24px" }}>
                    <p style={{ fontSize: 13, fontWeight: 800, color: "#fff", margin: "0 0 24px" }}>Signups — Last 14 Days</p>
                    {(() => {
                      const days = Object.entries(analyticsData.signupsByDay);
                      const maxVal = Math.max(1, ...days.map(([, v]) => v));
                      return (
                        <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 110 }}>
                          {days.map(([date, count]) => {
                            const barH = count > 0 ? Math.max(8, Math.round((count / maxVal) * 72)) : 4;
                            const d = new Date(date + "T12:00:00");
                            const label = `${d.getDate()}/${d.getMonth() + 1}`;
                            return (
                              <div key={date} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end", height: "100%", gap: 4 }}>
                                {count > 0 && <span style={{ fontSize: 9, color: "rgba(255,255,255,0.5)", fontWeight: 700 }}>{count}</span>}
                                <div style={{ width: "100%", height: barH, background: count > 0 ? "#dc2626" : "rgba(255,255,255,0.06)", borderRadius: "3px 3px 0 0" }} />
                                <span style={{ fontSize: 9, color: "rgba(255,255,255,0.25)", fontWeight: 500, paddingTop: 4 }}>{label}</span>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>

                  {/* SECTION 3 — MOST ACTIVE FUNDS */}
                  <div style={{ background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 14, overflow: "hidden" }}>
                    <div style={{ padding: "14px 20px", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                      <p style={{ fontSize: 13, fontWeight: 800, color: "#fff", margin: 0 }}>Most Active Funds</p>
                      <p style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", margin: 0 }}>Top 5 by total deck analyses</p>
                    </div>
                    {analyticsData.mostActiveUsers.length === 0 ? (
                      <div style={{ padding: "36px", textAlign: "center" }}>
                        <p style={{ fontSize: 12, color: "rgba(255,255,255,0.2)", margin: 0 }}>No usage data yet</p>
                      </div>
                    ) : (
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                          <tr style={{ background: "rgba(255,255,255,0.02)" }}>
                            {["Fund", "Role", "Total Analyses", "Last Active", "Status"].map((h, i) => (
                              <th key={i} style={{ padding: "10px 20px", textAlign: "left", fontSize: 9, fontWeight: 700, color: "rgba(255,255,255,0.25)", textTransform: "uppercase", letterSpacing: "0.1em" }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {analyticsData.mostActiveUsers.map((u, i) => {
                            const lastDate = u.lastActive ? new Date(u.lastActive) : null;
                            const daysSince = lastDate ? Math.floor((Date.now() - lastDate.getTime()) / (24 * 60 * 60 * 1000)) : null;
                            const dotColor = daysSince === null ? "#6b7280" : daysSince <= 7 ? "#16a34a" : daysSince <= 30 ? "#d97706" : "#6b7280";
                            const dotLabel = daysSince === null ? "No data" : daysSince <= 7 ? "Active" : daysSince <= 30 ? "Recent" : "Inactive";
                            return (
                              <tr key={i} style={{ borderTop: "1px solid rgba(255,255,255,0.04)" }}>
                                <td style={{ padding: "13px 20px" }}>
                                  <p style={{ fontSize: 12, fontWeight: 700, color: "#fff", margin: "0 0 2px" }}>{u.name}</p>
                                  <p style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", margin: 0 }}>{u.fund}</p>
                                </td>
                                <td style={{ padding: "13px 20px", fontSize: 12, color: "rgba(255,255,255,0.4)" }}>{u.role}</td>
                                <td style={{ padding: "13px 20px" }}>
                                  <span style={{ fontSize: 20, fontWeight: 900, color: "#dc2626" }}>{u.total}</span>
                                </td>
                                <td style={{ padding: "13px 20px", fontSize: 11, color: "rgba(255,255,255,0.35)" }}>
                                  {lastDate ? lastDate.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                                </td>
                                <td style={{ padding: "13px 20px" }}>
                                  <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 10, color: dotColor, fontWeight: 600 }}>
                                    <span style={{ width: 6, height: 6, background: dotColor, borderRadius: "50%", display: "inline-block", flexShrink: 0 }} />
                                    {dotLabel}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>

                  {/* SECTION 4 — FEATURE ADOPTION */}
                  <div style={{ background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 14, padding: "20px 24px" }}>
                    <p style={{ fontSize: 13, fontWeight: 800, color: "#fff", margin: "0 0 20px" }}>Feature Adoption</p>
                    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                      {[
                        { label: "Pitch Summariser", pct: analyticsData.featureAdoption.pitchSummariser },
                        { label: "Portfolio", pct: analyticsData.featureAdoption.portfolio },
                        { label: "Investment Memo", pct: analyticsData.featureAdoption.investmentMemo },
                        { label: "Fund Memory", pct: analyticsData.featureAdoption.fundMemory },
                      ].map((f, i) => (
                        <div key={i}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 7 }}>
                            <span style={{ fontSize: 12, fontWeight: 600, color: "rgba(255,255,255,0.65)" }}>{f.label}</span>
                            <span style={{ fontSize: 12, fontWeight: 800, color: "#dc2626" }}>{f.pct}%</span>
                          </div>
                          <div style={{ background: "rgba(255,255,255,0.05)", borderRadius: 100, height: 6, overflow: "hidden" }}>
                            <div style={{ width: `${f.pct}%`, height: "100%", background: "#dc2626", borderRadius: 100 }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* SECTION 5 — AT RISK USERS */}
                  <div style={{ background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 14, overflow: "hidden" }}>
                    <div style={{ padding: "14px 20px", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                      <p style={{ fontSize: 13, fontWeight: 800, color: "#fff", margin: 0 }}>At Risk Users</p>
                      <p style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", margin: 0 }}>Approved users with no activity in 14+ days</p>
                    </div>
                    {analyticsData.atRiskUsers.length === 0 ? (
                      <div style={{ padding: "36px", textAlign: "center" }}>
                        <p style={{ fontSize: 12, color: "#16a34a", margin: 0, fontWeight: 600 }}>No at-risk users — great engagement!</p>
                      </div>
                    ) : (
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                          <tr style={{ background: "rgba(255,255,255,0.02)" }}>
                            {["Name", "Fund", "Approved Date", "Last Seen"].map((h, i) => (
                              <th key={i} style={{ padding: "10px 20px", textAlign: "left", fontSize: 9, fontWeight: 700, color: "rgba(255,255,255,0.25)", textTransform: "uppercase", letterSpacing: "0.1em" }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {analyticsData.atRiskUsers.slice(0, 20).map((u, i) => (
                            <tr key={i} style={{ borderTop: "1px solid rgba(255,255,255,0.04)" }}>
                              <td style={{ padding: "13px 20px", fontSize: 12, fontWeight: 700, color: "#fff" }}>{u.full_name || "—"}</td>
                              <td style={{ padding: "13px 20px", fontSize: 12, color: "rgba(255,255,255,0.4)" }}>{u.fund_name || "—"}</td>
                              <td style={{ padding: "13px 20px", fontSize: 11, color: "rgba(255,255,255,0.35)" }}>
                                {u.created_at ? new Date(u.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                              </td>
                              <td style={{ padding: "13px 20px" }}>
                                {u.neverActive
                                  ? <span style={{ fontSize: 11, fontWeight: 700, color: "#dc2626" }}>Never active</span>
                                  : <span style={{ fontSize: 11, fontWeight: 700, color: "#d97706" }}>14+ days ago</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>

                </div>
              )}
            </div>
          )}

        </div>
      </main>
    </div>
  );
}
